import { Component, computed, input, linkedSignal, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import type { AgentQuestion } from '../colimind-types';

interface Selection {
  labels: string[];
  freeText: string;
}

/** Form for the agent's questions. Shows a read-only summary once `answers` is set. */
@Component({
  selector: 'colimo-agent-questions',
  standalone: true,
  imports: [FormsModule, MatButtonModule, MatCheckboxModule, MatRadioModule, TranslateModule],
  templateUrl: './colimind-agent-questions.component.html',
  styleUrls: ['./colimind-agent-questions.component.scss'],
})
export class AgentQuestionsComponent {
  readonly questions = input.required<AgentQuestion[]>();
  readonly answers = input<string[] | undefined>(undefined);
  readonly disabled = input(false);
  readonly answer = output<string[]>();

  readonly selections = linkedSignal<Selection[]>(() =>
    this.questions().map(() => ({ labels: [], freeText: '' })));

  readonly complete = computed(() => this.questions().every((q, i) =>
    !q.required || this.answerFor(i).length > 0));

  isChecked(index: number, label: string): boolean {
    return this.selections()[index]?.labels.includes(label) ?? false;
  }

  choose(index: number, label: string): void {
    this.update(index, s => ({ ...s, labels: [label] }));
  }

  toggle(index: number, label: string, checked: boolean): void {
    this.update(index, s => ({
      ...s,
      labels: checked ? [...s.labels, label] : s.labels.filter(l => l !== label),
    }));
  }

  setFreeText(index: number, text: string): void {
    this.update(index, s => ({ ...s, freeText: text }));
  }

  submit(): void {
    if (!this.complete() || this.disabled()) return;
    this.answer.emit(this.questions().map((_, i) => this.answerFor(i)));
  }

  private answerFor(index: number): string {
    const s = this.selections()[index];
    if (!s) return '';
    return [...s.labels, s.freeText.trim()].filter(Boolean).join(', ');
  }

  private update(index: number, change: (s: Selection) => Selection): void {
    this.selections.update(all => all.map((s, i) => i === index ? change(s) : s));
  }
}
